import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Upload, Users, Trash2, Search, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { devWarn } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [addModal, setAddModal] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  
  // New client form
  const [newClient, setNewClient] = useState({ name: "", pan: "", gstin: "", contact_person: "", email: "", phone: "" });
  
  const fileInputRef = useRef(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (err) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filter to only Employee and Customer users (not Admin)
      setUsers(response.data.filter(u => u.role !== "admin"));
    } catch (err) {
      /* users list is supplementary for client assignment UI */
      devWarn("fetchUsers (non-critical):", err);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchUsers();
  }, [fetchClients, fetchUsers]);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedClients(new Set(filteredClients.map(c => c.id)));
    } else {
      setSelectedClients(new Set());
    }
  };

  const handleSelectClient = (clientId, checked) => {
    const newSelected = new Set(selectedClients);
    if (checked) {
      newSelected.add(clientId);
    } else {
      newSelected.delete(clientId);
    }
    setSelectedClients(newSelected);
    setSelectAll(newSelected.size === filteredClients.length);
  };

  const handleAddClient = async () => {
    if (!newClient.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    
    try {
      await axios.post(`${API}/clients`, newClient);
      toast.success("Client added successfully");
      setAddModal(false);
      setNewClient({ name: "", pan: "", gstin: "", contact_person: "", email: "", phone: "" });
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add client");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      
      // Map Excel columns to client fields
      const clients = jsonData.map(row => ({
        name: row["Name"] || row["Client Name"] || row["name"] || "",
        pan: row["PAN"] || row["pan"] || "",
        gstin: row["GSTIN"] || row["gstin"] || "",
        contact_person: row["Contact Person"] || row["contact_person"] || "",
        email: row["Email"] || row["email"] || "",
        phone: row["Phone"] || row["phone"] || ""
      })).filter(c => c.name);
      
      if (clients.length === 0) {
        toast.error("No valid clients found in file");
        return;
      }
      
      const response = await axios.post(`${API}/clients/bulk`, { clients });
      
      toast.success(`Created: ${response.data.created}, Updated: ${response.data.updated}`);
      if (response.data.errors?.length > 0) {
      }
      
      setBulkUploadModal(false);
      fetchClients();
    } catch (err) {
      toast.error("Failed to upload clients: " + (err.response?.data?.detail || err.message));
    }
    
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleBulkAssign = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }
    if (selectedClients.size === 0) {
      toast.error("Please select clients to assign");
      return;
    }
    
    try {
      await axios.post(`${API}/users/bulk-assign-clients`, {
        user_id: selectedUserId,
        client_ids: Array.from(selectedClients),
        action: "assign"
      });
      
      toast.success(`Assigned ${selectedClients.size} client(s) to user`);
      setAssignModal(false);
      setSelectedUserId("");
      setSelectedClients(new Set());
      setSelectAll(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign clients");
    }
  };

  const downloadTemplate = () => {
    const template = [
      { "Name": "Example Company Pvt Ltd", "PAN": "ABCDE1234F", "GSTIN": "29ABCDE1234F1Z5", "Contact Person": "John Doe", "Email": "john@example.com", "Phone": "9876543210" }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");
    XLSX.writeFile(wb, "client_upload_template.xlsx");
  };

  const filteredClients = useMemo(() => clients.filter(c => 
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.pan?.toLowerCase().includes(search.toLowerCase())
  ), [clients, search]);

  const selectedClientsList = useMemo(() => 
    filteredClients.filter(c => selectedClients.has(c.id)),
    [filteredClients, selectedClients]
  );

  return (
    <div className="p-6" data-testid="client-management">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Client Management</h1>
          <p className="text-slate-500">Manage clients and assign to users</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload
          </Button>
          <Button onClick={() => setAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Client
          </Button>
        </div>
      </div>

      {/* Search and Bulk Actions */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search by name or PAN..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {selectedClients.size > 0 && (
              <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
                <span className="text-sm text-blue-700 font-medium">{selectedClients.size} selected</span>
                <Button size="sm" onClick={() => setAssignModal(true)}>
                  <Users className="w-4 h-4 mr-2" /> Assign to User
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setSelectedClients(new Set()); setSelectAll(false); }}>
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="w-10 p-3">
                    <Checkbox 
                      checked={selectAll} 
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="text-left p-3 font-medium text-slate-600">Name</th>
                  <th className="text-left p-3 font-medium text-slate-600">PAN</th>
                  <th className="text-left p-3 font-medium text-slate-600">GSTIN</th>
                  <th className="text-left p-3 font-medium text-slate-600">Contact</th>
                  <th className="text-left p-3 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.id} className="border-b hover:bg-slate-50">
                    <td className="p-3">
                      <Checkbox 
                        checked={selectedClients.has(client.id)}
                        onCheckedChange={(checked) => handleSelectClient(client.id, checked)}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{client.name}</div>
                      {client.email && <div className="text-xs text-slate-500">{client.email}</div>}
                    </td>
                    <td className="p-3 font-mono text-sm">{client.pan || "-"}</td>
                    <td className="p-3 text-sm">{client.gstin || "-"}</td>
                    <td className="p-3">
                      <div className="text-sm">{client.contact_person || "-"}</div>
                      {client.phone && <div className="text-xs text-slate-500">{client.phone}</div>}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        client.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>
                        {client.is_active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredClients.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      No clients found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Add Client Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>Enter client details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Client Name *</Label>
              <Input 
                value={newClient.name} 
                onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                placeholder="Enter client name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PAN</Label>
                <Input 
                  value={newClient.pan} 
                  onChange={(e) => setNewClient({...newClient, pan: e.target.value.toUpperCase()})}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input 
                  value={newClient.gstin} 
                  onChange={(e) => setNewClient({...newClient, gstin: e.target.value.toUpperCase()})}
                  placeholder="29ABCDE1234F1Z5"
                  maxLength={15}
                />
              </div>
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input 
                value={newClient.contact_person} 
                onChange={(e) => setNewClient({...newClient, contact_person: e.target.value})}
                placeholder="Contact name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={newClient.email} 
                  onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={newClient.phone} 
                  onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                  placeholder="9876543210"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddClient}>Add Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={bulkUploadModal} onOpenChange={setBulkUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload Clients</DialogTitle>
            <DialogDescription>Upload an Excel file with client details</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Excel file should have columns:</p>
              <p className="text-xs font-mono text-slate-500">Name, PAN, GSTIN, Contact Person, Email, Phone</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>
            </div>
            <input 
              type="file" 
              accept=".xlsx,.xls,.csv" 
              ref={fileInputRef}
              onChange={handleBulkUpload}
              className="hidden"
              id="bulk-upload-input"
            />
            <label htmlFor="bulk-upload-input">
              <Button as="span" className="w-full cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" /> Select File to Upload
              </Button>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign to User Modal */}
      <Dialog open={assignModal} onOpenChange={setAssignModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Clients to User</DialogTitle>
            <DialogDescription>Assign the selected clients to an Employee or Customer user</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Selected Clients Summary */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Selected Clients ({selectedClients.size}):</p>
              <div className="max-h-32 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {selectedClientsList.map(c => (
                      <span key={c.id} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {c.name}
                      </span>
                    ))}
                </div>
              </div>
            </div>
            
            {/* User Selection */}
            <div>
              <Label className="text-sm font-medium">Select User to Assign</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          user.role === 'employee' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {user.role}
                        </span>
                        <span>{user.full_name}</span>
                        <span className="text-slate-500">({user.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {users.length === 0 && (
                <p className="text-sm text-amber-600 mt-2">No Employee or Customer users found. Create users first in User Management.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign} disabled={!selectedUserId}>
              <Users className="w-4 h-4 mr-2" />
              Assign {selectedClients.size} Client(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientManagement;
